import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Button,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * UnsavedChangesGuard
 *
 * Usage:
 *   const guardRef = useRef();
 *
 *   <UnsavedChangesGuard ref={guardRef} hasChanges={isDirty}>
 *     <Button onClick={() => guardRef.current.navigate(() => navigate('/back'))}>Back</Button>
 *   </UnsavedChangesGuard>
 */
const UnsavedChangesGuard = forwardRef(({ hasChanges, children }, ref) => {
    const [open, setOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const showDialog = useCallback((action) => {
        setPendingAction(() => action);
        setOpen(true);
    }, []);

    // Expose navigate method via ref
    useImperativeHandle(ref, () => ({
        navigate: (action) => {
            if (hasChanges) {
                showDialog(action);
            } else {
                action();
            }
        },
    }), [hasChanges, showDialog]);

    const handleStay = () => {
        setOpen(false);
        setPendingAction(null);
    };

    const handleLeave = () => {
        setOpen(false);
        if (pendingAction) pendingAction();
        setPendingAction(null);
    };

    // Browser refresh / tab close
    useEffect(() => {
        const handler = (e) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasChanges]);

    // Browser back button
    useEffect(() => {
        if (!hasChanges) return;

        const handlePopState = () => {
            window.history.pushState(null, '', window.location.href);
            showDialog(() => window.history.go(-2));
        };

        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [hasChanges, showDialog]);

    return (
        <>
            {children}

            <Dialog open={open} onClose={handleStay} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon color="warning" />
                    Unsaved Changes
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have unsaved changes that will be lost if you leave this page.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleStay} variant="contained" color="secondary">
                        Stay
                    </Button>
                    <Button onClick={handleLeave} color="error">
                        Leave
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

export default UnsavedChangesGuard;
